import { FC, ReactNode } from 'react'

import { useNavigate } from 'react-router-dom'

import {
  Text,
  IconArrowLeft,
  Button,
  GreyGrey0,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyWhite,
  Primary13Regular,
  Skeleton,
  Spinner,
  Center,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'

import { useIsMobile } from 'hooks/useIsMobile'
import { useWatchtowerListPath } from 'Automations/watchtower/inbox/hooks/useWatchtowerListPath'
import { MobileActionBar } from '../MobileActionBar/MobileActionBar'

const InboxPreviewComponent: FC<{
  header: ReactNode
  // Primary CTAs. Shown in the header on desktop; on mobile they move to a
  // fixed bottom action bar as equal-width columns (a single action spans the
  // full width; two actions split it 50/50).
  actions?: ReactNode
  // Secondary controls (e.g. a kebab menu) that stay in the header top-right on
  // every viewport.
  headerActions?: ReactNode
  children: ReactNode
  'data-testid'?: string
}> = ({
  header,
  actions,
  headerActions,
  children,
  'data-testid': dataTestId = 'watchtower.executionSummary'
}) => {
  const isMobile = useIsMobile()
  const showBottomActionBar = isMobile && Boolean(actions)

  return (
    <Grid
      data-testid={dataTestId}
      height="100%"
      width="100%"
      minWidth={{ base: 0, md: '720px' }}
      overflow="hidden"
      gridTemplateRows="auto 1fr auto"
    >
      <Grid
        height="48px"
        padding="0 16px"
        width="100%"
        overflow="hidden"
        // Mobile: [back] [title] [header actions]. Desktop: [title] [actions];
        // the back button is hidden so its column drops out.
        gridTemplateColumns={{ base: 'auto 1fr auto', md: '1fr auto' }}
        columnGap="8px"
        alignItems="center"
        backgroundColor={GreyWhite}
        borderBottom={`1px solid ${GreyGrey30}`}
      >
        <MobilePreviewBackButton />
        <Box minWidth="0" overflow="hidden">
          {header}
        </Box>
        <Flex alignItems="center" gap="8px">
          {!isMobile && actions}
          {headerActions}
        </Flex>
      </Grid>
      <Box
        position="relative"
        height="100%"
        width="100%"
        overflowY="auto"
        overflowX="hidden"
        background={GreyWhite}
      >
        {children}
      </Box>
      {showBottomActionBar && (
        <MobileActionBar data-testid="watchtower.preview.actionBar">
          {actions}
        </MobileActionBar>
      )}
    </Grid>
  )
}

// Mobile-only "back to list" control. On a phone the inbox shows one pane at a
// time, so navigating to the list route clears the selected item and returns
// the user to the list. Hidden on desktop, where both panes are visible.
const MobilePreviewBackButton: FC = () => {
  const navigate = useNavigate()
  const listPath = useWatchtowerListPath()

  return (
    <Box display={{ base: 'flex', md: 'none' }}>
      <Button
        variant="ghost"
        aria-label="Back to inbox"
        onClick={() => navigate(listPath)}
        icon={<IconArrowLeft size={20} color={GreyGrey60} />}
        data-testid="watchtower.preview.back"
      />
    </Box>
  )
}

const EmptyState: FC<{ children?: ReactNode }> = ({ children }) => {
  return (
    <Center height="100%" width="100%" background={GreyGrey0}>
      {children && (
        <Text {...Primary13Regular} color={GreyGrey70}>
          {children}
        </Text>
      )}
    </Center>
  )
}

// Mirrors the structure of a loaded preview (header + content) so the layout
// doesn't jump when data arrives. The header shows skeleton placeholders for
// the task icon and title; the content keeps the centred spinner.
const LoadingState: FC = () => {
  return (
    <InboxPreviewComponent
      data-testid="watchtower.preview.loading"
      header={
        <Flex alignItems="center" gap="8px">
          <Skeleton height="32px" width="32px" />
          <Skeleton height="16px" width="160px" />
        </Flex>
      }
    >
      <Center height="100%" width="100%">
        <Spinner />
      </Center>
    </InboxPreviewComponent>
  )
}

export const InboxPreview = Object.assign(InboxPreviewComponent, {
  EmptyState,
  LoadingState
})
